import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../config/constants";

const AUTH_KEY = "barrista_admin_auth";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(AUTH_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.token) setAuth(parsed);
      }
    } catch (e) {
      localStorage.removeItem(AUTH_KEY);
    }
  }, []);

  const persistAuth = useCallback((data) => {
    if (data?.token) {
      localStorage.setItem(AUTH_KEY, JSON.stringify(data));
      setAuth(data);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_KEY);
    setAuth(null);
  }, []);

  const loginSuperAdmin = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API_URL}/graphql`, {
      query: `
        mutation($email: String!, $password: String!) {
          loginSuperAdmin(email: $email, password: $password) {
            token
            role
            venue_id
            email
          }
        }
      `,
      variables: { email, password },
    });
    if (data.errors) throw new Error(data.errors[0]?.message || "Login failed");
    const payload = data.data.loginSuperAdmin;
    if (!payload?.token) throw new Error("Invalid response");
    persistAuth({ token: payload.token, role: payload.role, venueId: payload.venue_id, email: payload.email });
    return payload;
  }, [persistAuth]);

  const loginVenueAdmin = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API_URL}/graphql`, {
      query: `
        mutation($email: String!, $password: String!) {
          loginVenueAdmin(email: $email, password: $password) {
            token
            role
            venue_id
            email
          }
        }
      `,
      variables: { email, password },
    });
    if (data.errors) throw new Error(data.errors[0]?.message || "Login failed");
    const payload = data.data.loginVenueAdmin;
    if (!payload?.token) throw new Error("Invalid response");
    persistAuth({ token: payload.token, role: payload.role, venueId: payload.venue_id, email: payload.email });
    return payload;
  }, [persistAuth]);

  const api = useCallback((query, variables = {}) => {
    if (!auth?.token) throw new Error("Not authenticated");
    return axios.post(
      `${API_URL}/graphql`,
      { query, variables },
      { headers: { Authorization: `Bearer ${auth.token}` } }
    );
  }, [auth?.token]);

  const value = useMemo(
    () => ({
      auth,
      loginSuperAdmin,
      loginVenueAdmin,
      logout,
      api,
      isSuperAdmin: auth?.role === "super_admin",
      isVenueAdmin: auth?.role === "venue_admin",
      venueId: auth?.venueId ?? auth?.venue_id,
    }),
    [auth, api, loginSuperAdmin, loginVenueAdmin, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
